import { createTheme } from '@mui/material/styles';

// Warning Dashboard visual system - lifted from the CRO playbook reference: warm cream canvas, white
// cards with a hairline border, dark-green accent, serif headings over a clean sans body, small tinted
// chips, and a purple-bordered "highlight" card. Applied ONLY inside WarningThemeScope (see
// WarningThemeLayout.jsx), so the rest of the portal keeps its own theme.
//
// Severity colors (warning/clear/info) deliberately stay semantic - the new look only restyles the
// chrome around them, not what red/green/blue mean.
export const WD = {
  canvas: '#FFFFFF', // plain white page - the cards carry their own colour now
  card: '#FFFFFF',
  border: '#E6E2D9',
  ink: '#1A1A1A',
  inkMuted: '#6B6B66',
  inkFaint: '#9A9A93',
  green: '#1F5C4A', // dark-green accent: links, eyebrow text, outlined buttons
  greenTint: '#E3EFE8', // chip background
  navy: '#1B3A6B',
  brown: '#6B4E16',
  purple: '#5B3FA6', // highlight border
  purpleTint: '#EDE7F8',
  badge: '#1A1A1A', // numbered circle
};

export const SEVERITY = {
  warning: { accent: '#D92D20', pillBg: '#FEF3F2', pillText: '#B42318' },
  clear: { accent: '#1F8A5B', pillBg: '#E3F3EA', pillText: '#146C43' },
  info: { accent: '#5B3FA6', pillBg: '#EDE7F8', pillText: '#4A3190' },
  neutral: { accent: '#CFCBC0', pillBg: '#EFEDE6', pillText: '#6B6B66' },
};

export const FONT_SERIF = '"Fraunces", "Lora", Georgia, serif';
export const FONT_SANS = '"Inter", "Public Sans", system-ui, -apple-system, sans-serif';

const warningTheme = (outer) =>
  createTheme(outer, {
    palette: {
      primary: { main: WD.green },
      error: { main: '#D92D20' },
      success: { main: '#1F8A5B' },
      text: { primary: WD.ink, secondary: WD.inkMuted },
      background: { default: WD.canvas, paper: WD.card },
      divider: WD.border,
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: FONT_SANS,
      h1: { fontFamily: FONT_SERIF, fontWeight: 600 },
      h2: { fontFamily: FONT_SERIF, fontWeight: 600 },
      h3: { fontFamily: FONT_SERIF, fontWeight: 600 },
      h4: { fontFamily: FONT_SERIF, fontWeight: 600 },
      h5: { fontFamily: FONT_SERIF, fontWeight: 600 },
      h6: { fontFamily: FONT_SERIF, fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none', border: `1px solid ${WD.border}`, boxShadow: 'none' } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 14, textTransform: 'none', fontWeight: 600 },
          outlined: { borderWidth: 2, '&:hover': { borderWidth: 2, backgroundColor: WD.greenTint } },
          contained: { backgroundColor: WD.green, '&:hover': { backgroundColor: '#174536' } },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
      MuiTextField: { defaultProps: { variant: 'outlined' } },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 12, backgroundColor: WD.card, '& fieldset': { borderColor: WD.border } } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${WD.border}` },
          head: { backgroundColor: '#FAF8F3', position: 'sticky', top: 0, zIndex: 1, color: WD.inkMuted, fontWeight: 600, fontSize: 12, letterSpacing: 0.3 },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 14, border: `1px solid ${WD.border}`, backgroundColor: WD.card, maxHeight: '70vh' } } },
      MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: '#FAF8F3' } } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 18 } } },
    },
  });

export default warningTheme;
